import { Box, Button, ListItem, Tag, Typography } from "@luce/ui";

import { GiftCardStatusEnum } from "../../../__generated__/graphql";
import { GIFT_CARD_STATUS } from "../../lib";
import type { GiftCard } from "../lib";
import { sentStatusMessage } from "../lib";
import { GiftCardHero } from "./gift-card-hero";

interface SentGiftDetailProps {
  giftCard: GiftCard;
  isCorporate: boolean;
  onCopyCode: () => void;
  onShare: () => void;
  onViewInvoice: () => void;
}

export function SentGiftDetail({
  giftCard,
  isCorporate,
  onCopyCode,
  onShare,
  onViewInvoice,
}: SentGiftDetailProps) {
  const status = GIFT_CARD_STATUS[giftCard.status];
  const isActive = giftCard.status === GiftCardStatusEnum.Active;
  const isPendingPayment = giftCard.status === GiftCardStatusEnum.PendingPayment;

  return (
    <Box className="flex flex-col">
      <Box className="px-spacing-lg pb-spacing-sm pt-spacing-lg">
        <GiftCardHero occasion={giftCard.occasionType} />
      </Box>

      <ListItem>
        <ListItem.Content>
          <ListItem.ContentSupportingText>Status</ListItem.ContentSupportingText>
          <ListItem.ContentTitle>{sentStatusMessage(giftCard)}</ListItem.ContentTitle>
        </ListItem.Content>
        <ListItem.Trailing>
          <Tag variant={status.variant}>{status.label}</Tag>
        </ListItem.Trailing>
      </ListItem>

      {isActive && (
        <ListItem>
          <ListItem.Content>
            <ListItem.ContentSupportingText>Gift Code</ListItem.ContentSupportingText>
            <ListItem.ContentTitle>{giftCard.code}</ListItem.ContentTitle>
          </ListItem.Content>
          <ListItem.Trailing className="gap-spacing-sm flex flex-row">
            <Button variant="secondary" size="md" label="Copy" onClick={onCopyCode} />
            <Button
              variant="primary"
              size="md"
              label="Share"
              trailingIcon="ShareFat"
              onClick={onShare}
            />
          </ListItem.Trailing>
        </ListItem>
      )}

      <ListItem>
        <ListItem.Content>
          <ListItem.ContentSupportingText>
            {isCorporate ? "Amount" : "Gift Sent"}
          </ListItem.ContentSupportingText>
          <Typography variant="heading-h3" color="brand-primary">
            ${giftCard.amount}
          </Typography>
        </ListItem.Content>
        {giftCard.invoice && (
          <ListItem.Trailing>
            {isPendingPayment ? (
              <Button variant="primary" size="sm" label="Pay" onClick={onViewInvoice} />
            ) : (
              <Button
                variant="tertiary"
                size="sm"
                label="View Invoice"
                trailingIcon="CaretRight"
                onClick={onViewInvoice}
              />
            )}
          </ListItem.Trailing>
        )}
      </ListItem>

      {isCorporate && giftCard.expirationDate && (
        <ListItem>
          <ListItem.Content>
            <ListItem.ContentSupportingText>Expiration Date</ListItem.ContentSupportingText>
            <ListItem.ContentTitle>12 months after redeem</ListItem.ContentTitle>
          </ListItem.Content>
        </ListItem>
      )}

      <ListItem>
        <ListItem.Content>
          <ListItem.ContentSupportingText>Sender</ListItem.ContentSupportingText>
          <ListItem.ContentTitle>{giftCard.senderName}</ListItem.ContentTitle>
        </ListItem.Content>
      </ListItem>

      <ListItem divider={false}>
        <ListItem.Content>
          <ListItem.ContentSupportingText>Message</ListItem.ContentSupportingText>
          <Typography variant="label-md">{giftCard.messageTitle}</Typography>
          <ListItem.ContentSupportingText>{giftCard.messageBody}</ListItem.ContentSupportingText>
        </ListItem.Content>
      </ListItem>
    </Box>
  );
}
